import { gql, useApolloClient } from "@apollo/client";
import { v4 as uuid } from "uuid";
import { AddVariantFragmentDoc, AddAddonFragmentDoc, UpdateSubscriptionBenefitFragmentDoc, UpdateDriveBenefitFragmentDoc, UpdateWelcomeGiftBenefitFragmentDoc, UpdateSubscriptionBenefitShippingFrequencyFragmentDoc, SubscriptionBenefitProductFragmentDoc, DeliveryStrategy, CurationStatus } from "../types";
// import {createFilter} from "react-select"
gql `
fragment SubscriptionBenefitPreferNetwork on SubscriptionBenefit {
  id
  isAddonOnly
  products {
    ...SubscriptionBenefitProduct
  }
  variants {
    ...VariantPanel
  }
  addons {
    ...AddonPanel
  }
  recentFulfillments {
    platformLineItems {
      platformProductVariantId
      swapped
    }
  }
  shopifyProductIds  
  business {
    id
    enableAutomatedCuration
    enableProductSwapping
    includeWelcomeGiftInFirstShipment
    status
    shopifyShop
  }
}

fragment SubscriptionBenefitPreferCache on SubscriptionBenefit {
  id
  name
  membershipTiers {
    id
    name
    members(status: Active) {
      id
    }
    plans {
      kind
      price {
        amount
      }
    }
  }
  delivery
  deliveryFrequency
  shippingFrequency {
    number
    period
  }
  firstShipmentDelay
  nextShipmentDate
  shippingNotesInstructions
  curationPreferOverstock
  curationMatchMode
  pickupEnabled
  shippingEnabled
  shippingCost {
    amount
  }
}

fragment DriveBenefitPreferNetwork on DriveBenefit {
  id
  products {
    ...SubscriptionBenefitProduct
  }
  business {
    id
    enableAutomatedCuration
    enableProductSwapping
    includeWelcomeGiftInFirstShipment
    status
  }
}

fragment DriveBenefitPreferCache on DriveBenefit {
  id
  name
  shippingNotesInstructions
}

fragment WelcomeGiftBenefitPreferNetwork on WelcomeGiftBenefit {
  id
  products {
    ...SubscriptionBenefitProduct
  }
  variants {
    ...VariantPanel
  }
  shopifyProductIds  
  business {
    id
    enableAutomatedCuration
    enableProductSwapping
    includeWelcomeGiftInFirstShipment
    status
  }
}

fragment WelcomeGiftBenefitPreferCache on WelcomeGiftBenefit {
  id
  name
  membershipTiers {
    id
    name
  }
  delivery
  shippingNotesInstructions
}

query GetBenefitAll($id: ID!){
  benefit(id: $id){
    ... on SubscriptionBenefit {
      ...SubscriptionBenefitPreferNetwork
      ...SubscriptionBenefitPreferCache
    }
    ... on WelcomeGiftBenefit {
      ...WelcomeGiftBenefitPreferNetwork
      ...WelcomeGiftBenefitPreferCache
    }
    ... on DriveBenefit {
      ...DriveBenefitPreferNetwork
      ...DriveBenefitPreferCache
    }
  }
}

query GetBenefitPreferNetwork($id: ID!) {
  benefit(id: $id){
    ... on SubscriptionBenefit {
      ...SubscriptionBenefitPreferNetwork
    }
    ... on WelcomeGiftBenefit {
      ...WelcomeGiftBenefitPreferNetwork
    }
    ... on DriveBenefit {
      ...DriveBenefitPreferNetwork
    }
  }
}

query GetBenefitPreferCache($id: ID!) {
  benefit(id: $id){
    ... on SubscriptionBenefit {
      ...SubscriptionBenefitPreferCache
    }
    ... on WelcomeGiftBenefit {
      ...WelcomeGiftBenefitPreferCache
    }
    ... on DriveBenefit {
      ...DriveBenefitPreferCache
    }
  }
}
`;
export const useUpdateIsAddonOnly = (benefitId) => {
    const client = useApolloClient();
    return (ev) => {
        // mixpanel.track("Subscription Box", {
        //   action: "Updated Is Addon Only",
        //   value: ev.target.value,
        // })
        if (ev.target.checked) {
            client.cache.modify({
                id: `SubscriptionBenefit:${benefitId}`,
                fields: {
                    isAddonOnly: () => true,
                },
            });
        }
        else {
            client.cache.modify({
                id: `SubscriptionBenefit:${benefitId}`,
                fields: {
                    isAddonOnly: () => false,
                },
            });
        }
    };
};
export const useUpdateWelcomeGift = (businessId) => {
    const client = useApolloClient();
    return (value) => {
        client.cache.modify({
            id: `Business:${businessId}`,
            fields: {
                "includeWelcomeGiftInFirstShipment": () => value,
            },
        });
    };
};
export const useUpdateBusiness = (businessId, field) => {
    const client = useApolloClient();
    return (ev) => {
        const value = !!ev.target.checked;
        client.cache.modify({
            id: `Business:${businessId}`,
            fields: {
                [field]: () => value,
            },
        });
    };
};
export const useUpdateCurationPreferOverstock = (benefitId) => {
    const client = useApolloClient();
    return (ev) => {
        // mixpanel.track("Subscription Box", {
        //   action: "Updated Curation Prefer Overstock",
        //   value: ev.target.value,
        // })
        if (ev.target.checked) {
            client.cache.modify({
                id: `SubscriptionBenefit:${benefitId}`,
                fields: {
                    curationPreferOverstock: () => true,
                },
            });
        }
        else {
            client.cache.modify({
                id: `SubscriptionBenefit:${benefitId}`,
                fields: {
                    curationPreferOverstock: () => false,
                },
            });
        }
    };
};
export const useAddProduct = ({ benefitId, benefitKind }) => {
    const client = useApolloClient();
    return () => {
        // mixpanel.track("Subscription Box", {
        //   action: "Added Product",
        // })
        const newProduct = {
            __typename: "Product",
            id: uuid(),
            name: "",
            platformId: "",
            platformName: "",
            platformVariantsOnlyMode: false,
            variants: [{
                    __typename: "Variant",
                    id: uuid(),
                    description: "Standard",
                    additionalCost: {
                        amount: 0,
                    },
                    // KGAUDIT - this probably shouldn't be shopify unless it is.
                    platformName: "shopify",
                    platformId: "",
                    curationExcludeProducts: [],
                    curationIncludeVariants: [],
                    curationSearchTerms: "",
                    curationStatus: "APPROVED",
                    curationError: null,
                    flagReason: null,
                }],
            curationValue: null,
            curationCollections: [],
            curationExcludeProducts: [],
        };
        client.cache.modify({
            id: `${benefitKind}:${benefitId}`,
            fields: {
                products(existingProductRefs = [], { readField }) {
                    if (!existingProductRefs) {
                        existingProductRefs = [];
                    }
                    const newProductRef = client.cache.writeFragment({
                        fragment: SubscriptionBenefitProductFragmentDoc,
                        fragmentName: "SubscriptionBenefitProduct",
                        data: newProduct,
                    });
                    // KGAUDIT - this actually makes no sense to me, why would there be a collision with a new uuid()?
                    if (existingProductRefs.some((ref) => readField("id", ref) === newProduct.id)) {
                        return existingProductRefs;
                    }
                    return [...existingProductRefs, newProductRef];
                },
            },
        });
    };
};
gql `
fragment AddVariant on Variant {
  id
  description
  additionalCost {
    amount
  }
  platformName
  platformId
  curationExcludeProducts
  curationIncludeVariants
  curationSearchTerms
  curationStatus
  curationError
  flagReason
}
`;
export const useAddVariant = (benefitId, benefitKind) => {
    const client = useApolloClient();
    return () => {
        // mixpanel.track("Subscription Box", {
        //   action: "Added Variant",
        // })
        const newVariant = {
            id: uuid(),
            __typename: "Variant",
            description: "",
            additionalCost: {
                amount: 0,
            },
            platformName: "",
            platformId: "",
            curationExcludeProducts: [],
            curationIncludeVariants: [],
            curationSearchTerms: "",
            curationStatus: "APPROVED",
            curationError: null,
            flagReason: null,
        };
        client.cache.modify({
            id: `${benefitKind}:${benefitId}`,
            fields: {
                variants(existingVariantRefs = [], { readField }) {
                    if (!existingVariantRefs) {
                        existingVariantRefs = [];
                    }
                    const newVariantRef = client.cache.writeFragment({
                        fragment: AddVariantFragmentDoc,
                        data: newVariant,
                    });
                    // KGAUDIT - also impossible
                    if (existingVariantRefs.some((ref) => readField("id", ref) === newVariant.id)) {
                        return existingVariantRefs;
                    }
                    return [...existingVariantRefs, newVariantRef];
                },
            },
        });
    };
};
gql `
fragment AddAddon on Addon {
  id
  description
  additionalCost {
    amount
  }
  applyDiscount
  dontRollOver
  platformId
  platformName
}
`;
export const useAddAddon = (benefitId) => {
    const client = useApolloClient();
    return () => {
        // mixpanel.track("Subscription Box", {
        //   action: "Added Addon",
        // })
        const newAddon = {
            id: uuid(),
            __typename: "Addon",
            description: "",
            additionalCost: {
                amount: 0
            },
            applyDiscount: true,
            dontRollOver: false,
            platformId: "",
            platformName: "",
        };
        client.cache.modify({
            id: `SubscriptionBenefit:${benefitId}`,
            fields: {
                addons(existingAddonRefs = [], { readField }) {
                    if (!existingAddonRefs) {
                        existingAddonRefs = [];
                    }
                    const newAddonRef = client.cache.writeFragment({
                        fragment: AddAddonFragmentDoc,
                        data: newAddon,
                    });
                    if (existingAddonRefs.some(ref => readField('id', ref) === newAddon.id)) {
                        return existingAddonRefs;
                    }
                    return [...existingAddonRefs, newAddonRef];
                }
            }
        });
    };
};
gql `

fragment UpdateSubscriptionBenefit on SubscriptionBenefit {
  name
  delivery
  nextShipmentDate
  deliveryFrequency
  firstShipmentDelay
  shippingFrequency {
    number
    period
  }
  shippingNotesInstructions
  curationPreferOverstock
  curationMatchMode
  pickupEnabled
  shippingEnabled
  shippingCost {
    amount
  }
}

fragment UpdateWelcomeGiftBenefit on WelcomeGiftBenefit {
  name
  shippingNotesInstructions
}

fragment UpdateDriveBenefit on DriveBenefit {
  name
  shippingNotesInstructions
}
`;
export const deliveryValuesFor = (delivery) => {
    switch (delivery) {
        case "ANNUAL":
            return {
                deliveryFrequency: 1,
                shippingFrequency: {
                    number: 1,
                    period: "YEAR",
                },
                firstShipmentDelay: 0
            };
            break;
        case "QUARTERLY":
            return {
                deliveryFrequency: 3,
                shippingFrequency: {
                    number: 3,
                    period: "MONTH",
                },
                firstShipmentDelay: 0
            };
            break;
        case "MONTHLY":
            return {
                deliveryFrequency: 1,
                shippingFrequency: {
                    number: 1,
                    period: "MONTH",
                },
                firstShipmentDelay: 0
            };
            break;
        case "BIWEEKLY":
            return {
                deliveryFrequency: 2,
                shippingFrequency: {
                    number: 2,
                    period: "WEEK",
                },
                firstShipmentDelay: 0
            };
        case "WEEKLY":
            return {
                deliveryFrequency: 1,
                shippingFrequency: {
                    number: 1,
                    period: "WEEK",
                },
                firstShipmentDelay: 0
            };
        case "IMMEDIATELY":
        case "CUSTOM":
        default:
            return {};
            break;
    }
};
export const useUpdateSubscriptionBenefit = (benefitId, field, benefitKind) => {
    const client = useApolloClient();
    const onClick = (ev) => {
        const existing = client.readFragment({
            id: `${benefitKind}:${benefitId}`,
            fragment: benefitKind == "SubscriptionBenefit" ? UpdateSubscriptionBenefitFragmentDoc : benefitKind == "WelcomeGiftBenefit" ? UpdateWelcomeGiftBenefitFragmentDoc : UpdateDriveBenefitFragmentDoc,
        });
        let value;
        switch (field) {
            case "deliveryFrequency":
            case "firstShipmentDelay":
                value = parseInt(ev.target.value) || 0;
                break;
            case "shippingCost":
                value = { amount: parseFloat(ev.target.value) || 0 };
                break;
            case "shippingEnabled":
            case "pickupEnabled":
                // @ts-ignore
                value = !!ev.target.checked;
                break;
            default:
                value = ev.target.value;
                break;
        }
        let newData = JSON.parse(JSON.stringify(existing));
        newData[field] = value;
        if (field == 'delivery')
            newData = Object.assign(Object.assign({}, newData), deliveryValuesFor(ev.target.value));
        client.writeFragment({
            id: `${benefitKind}:${benefitId}`,
            fragment: benefitKind == "SubscriptionBenefit" ? UpdateSubscriptionBenefitFragmentDoc : benefitKind == "WelcomeGiftBenefit" ? UpdateWelcomeGiftBenefitFragmentDoc : UpdateDriveBenefitFragmentDoc,
            data: newData,
        });
    };
    const onBlur = (ev) => {
        // mixpanel.track("Subscription Box", {
        //   action: "Edited Subscription Benefit",
        //   field: field,
        //   value: ev.target.value,
        // })
    };
    return [onClick, onBlur];
};
gql `
fragment UpdateSubscriptionBenefitShippingFrequency on SubscriptionBenefit {
  shippingFrequency {
    number
    period
  }
}
`;
export const useUpdateShippingFrequency = (benefitId, field) => {
    const client = useApolloClient();
    return (ev) => {
        const existing = client.readFragment({
            id: `SubscriptionBenefit:${benefitId}`,
            fragment: UpdateSubscriptionBenefitShippingFrequencyFragmentDoc,
        });
        const value = field === 'number' ? parseInt(ev.target.value) : ev.target.value;
        client.writeFragment({
            id: `SubscriptionBenefit:${benefitId}`,
            fragment: UpdateSubscriptionBenefitShippingFrequencyFragmentDoc,
            data: {
                shippingFrequency: Object.assign(Object.assign({}, existing.shippingFrequency), { [field]: value })
            },
        });
    };
};
export const prepareSubscriptionBenefitInput = (benefit, enableAutomatedCuration, defaultCurationValue) => {
    // Update custom values to preset values based on the selected frequency
    let deliveryFrequency = null, shippingFrequency = null, firstShipmentDelay = null;
    let delivery = benefit.delivery;
    switch (delivery) {
        // Retain custom values
        case DeliveryStrategy.Custom:
            deliveryFrequency = benefit.deliveryFrequency;
            shippingFrequency = {
                number: benefit.shippingFrequency.number,
                period: benefit.shippingFrequency.period,
            };
            firstShipmentDelay = benefit.firstShipmentDelay;
            break;
        // Use preset values
        case DeliveryStrategy.Annual:
            deliveryFrequency = 1;
            shippingFrequency = {
                number: 1,
                period: "YEAR",
            };
            firstShipmentDelay = 0;
            break;
        case DeliveryStrategy.Monthly:
            deliveryFrequency = 1;
            shippingFrequency = {
                number: 1,
                period: "MONTH",
            };
            firstShipmentDelay = 0;
            break;
        case DeliveryStrategy.Biweekly:
            deliveryFrequency = 2;
            shippingFrequency = {
                number: 2,
                period: "WEEK",
            };
            firstShipmentDelay = 0;
            break;
        case DeliveryStrategy.Weekly:
            deliveryFrequency = 1;
            shippingFrequency = {
                number: 1,
                period: "WEEK",
            };
            firstShipmentDelay = 0;
            break;
        case DeliveryStrategy.Immediately:
            break;
        case DeliveryStrategy.Quarterly:
        default:
            delivery = DeliveryStrategy.Quarterly;
            deliveryFrequency = 3;
            shippingFrequency = {
                number: 3,
                period: "MONTH",
            };
            firstShipmentDelay = 0;
            break;
    }
    return Object.assign({ id: benefit.id, name: benefit.name || "Curated item from our collection", shippingNotesInstructions: benefit.shippingNotesInstructions, shippingEnabled: benefit.shippingEnabled, pickupEnabled: benefit.pickupEnabled, shippingCost: benefit.shippingCost, isAddonOnly: benefit.isAddonOnly, alias: benefit.alias || "", products: (benefit.products || []).map(product => ({
            id: product.id,
            name: product.name || "Subscription Box Preference",
            platformName: !!product.platformId ? "shopify" : "",
            platformId: product.platformId,
            platformVariantsOnlyMode: !!product.platformVariantsOnlyMode,
            curationValue: product.curationValue || defaultCurationValue,
            curationCollections: product.curationCollections,
            curationExcludeProducts: product.curationExcludeProducts,
            variants: (product.variants || []).map(variant => ({
                id: variant.id,
                description: variant.description || "Standard",
                additionalCost: {
                    amount: variant.additionalCost.amount
                },
                platformName: !!variant.platformId ? "shopify" : "",
                platformId: variant.platformId,
                curationExcludeProducts: variant.curationExcludeProducts,
                curationIncludeVariants: variant.curationIncludeVariants,
                curationSearchTerms: variant.curationSearchTerms,
                curationStatus: enableAutomatedCuration ? variant.curationStatus : CurationStatus.Approved,
                curationError: enableAutomatedCuration ? variant.curationError : null,
                flagReason: enableAutomatedCuration ? variant.flagReason : null,
            }))
        })), variants: (benefit.variants || []).map(variant => ({
            id: variant.id,
            description: variant.description,
            additionalCost: {
                amount: variant.additionalCost.amount
            },
            // TODO: This should rule out variants for enable curation too but it's outdated code so i didn't fix
            platformName: !!variant.platformId ? "shopify" : "",
            platformId: variant.platformId,
        })) }, (delivery != DeliveryStrategy.Immediately) && {
        nextShipmentDate: benefit.nextShipmentDate,
        delivery: delivery,
        deliveryFrequency: deliveryFrequency,
        shippingFrequency: shippingFrequency,
        firstShipmentDelay: firstShipmentDelay,
        curationPreferOverstock: benefit.curationPreferOverstock,
        curationMatchMode: benefit.curationMatchMode,
        addons: (benefit.addons || []).map(addon => ({
            id: addon.id,
            description: addon.description,
            dontRollOver: addon.dontRollOver,
            additionalCost: {
                amount: addon.additionalCost.amount
            },
            applyDiscount: addon.applyDiscount,
            platformId: addon.platformId,
            platformName: addon.platformName,
        })),
    });
};
